import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';

const ProcessSection = () => {
  const { t } = useTranslation();

  const steps = [
    {
      number: '01',
      title: 'Analyse & Anforderungen',
      description: 'Wir analysieren gemeinsam deine aktuellen Prozesse und definieren, welche Funktionen dein Außendienst im Salescockpit benötigt.',
      features: [
        'Individuelle Prozessanalyse',
        'Erhebung der technischen Anforderungen',
        'Identifikation von Schnittstellen zum ERP',
        'Definition der benötigten Funktionen'
      ]
    },
    {
      number: '02',
      title: 'Konfiguration & Anpassung',
      description: 'Wir passen Salescockpit an deine Anforderungen an und richten die Schnittstelle zu deinem bestehenden ERP-System ein.',
      features: [
        'Anpassung der Benutzeroberfläche',
        'Integration mit deinem ERP-System',
        'Einrichtung kundenspezifischer Preislisten',
        'Konfiguration von Berechtigungen'
      ]
    },
    {
      number: '03',
      title: 'Testphase & Schulung',
      description: 'Eine kleine Gruppe deiner Mitarbeiter testet Salescockpit, während wir gleichzeitig Schulungen für das gesamte Team durchführen.',
      features: [
        'Begleiteter Testbetrieb',
        'Umfassende Schulungen für Endanwender',
        'Administrator-Training',
        'Optimierung basierend auf erstem Feedback'
      ]
    },
    {
      number: '04',
      title: 'Go-Live & kontinuierliche Optimierung',
      description: 'Nach dem erfolgreichen Rollout begleiten wir dich weiter und optimieren Salescockpit kontinuierlich auf Basis deiner Erfahrungen.',
      features: [
        'Begleiteter Go-Live',
        'Laufender Support',
        'Regelmäßige Updates',
        'Kontinuierliche Verbesserung basierend auf Nutzerfeedback'
      ]
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-blue-900">
            Dein Weg zum digitalen Außendienst
          </h2>
          <p className="text-xl text-gray-700">
            In nur 4 Schritten führen wir dich zur erfolgreichen Implementierung von Salescockpit in deinem Unternehmen.
          </p>
        </div>

        <div className="relative">
          {/* Vertikale Linie für Desktop */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-1 bg-blue-200"></div>
          
          <div className="space-y-12 relative">
            {steps.map((step, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                {/* Nummer + Verbindungslinie für Mobile */}
                <div className="md:hidden flex items-center justify-center mb-6">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-600 text-white font-bold text-xl">
                    {step.number}
                  </div>
                </div>
                
                {/* Inhalt */}
                <div className={`flex-1 ${index % 2 === 0 ? 'md:pr-12 lg:pr-16' : 'md:pl-12 lg:pl-16'}`}>
                  <div className="bg-blue-50 rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow">
                    <h3 className="text-2xl font-bold mb-4 text-blue-800">{step.title}</h3>
                    <p className="text-gray-700 mb-6">{step.description}</p>
                    <ul className="space-y-2">
                      {step.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start">
                          <svg className="h-5 w-5 text-blue-600 mr-2 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                {/* Nummer + Verbindungslinie für Desktop */}
                <div className="hidden md:flex items-center justify-center relative">
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-600 text-white font-bold text-2xl z-10">
                    {step.number}
                  </div>
                  <div className="absolute w-8 h-1 bg-blue-200 left-auto right-auto"></div>
                </div>
                
                {/* Leere zweite Spalte für Desktop */}
                <div className="hidden md:block flex-1"></div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="max-w-3xl mx-auto text-center mt-16">
          <p className="text-xl font-semibold text-blue-700">
            Typische Implementierungsdauer: Nur 3-6 Wochen
          </p>
          <p className="mt-4 text-gray-700">
            Je nach Umfang und Komplexität der ERP-Anbindung kann Salescockpit bereits nach wenigen Wochen produktiv eingesetzt werden.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection; 